/* ==================================================================
   GoSats · Go For Gold — campaign rules, states and copy tables
   - Streak: 7 daily Gold SIPs → 2% of gold bought, back in SuperCoins
   - First missed day  → window auto-extends by 1 (day 8 unlocks)
   - Second missed day → streak breaks immediately, no reward
   - Joining window    → Aug 11–14 only. Campaign itself runs to Aug 22

   All of this is mock/static data for now. When APIs land, only this
   module (state hydration) should need to change — the UI reads the
   same shapes.
   ================================================================== */

export const RATE = 50;            // ₹ per mg of gold
export const MIN = 200;            // minimum qualifying SIP
export const DAILY_TARGET = 1000;
export const TARGET = 7;           // SIPs required for the reward — never changes
export const CAMPAIGN_START = new Date('2026-08-14T00:00:00+05:30');
export const JOIN_CLOSE = new Date('2026-08-16T23:59:59+05:30');
export const CAMPAIGN_END = new Date('2026-08-26T23:59:59+05:30');
export const CLAIM_CLOSE = 'Aug 29th';

export const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

/* ---------------- state presets (every UI state) ---------------- */
export const INITIAL_STATE = {
  phase: 'join_open', // teaser | join_open | join_last | join_closed | final48 | ended
  joined: false,
  done: 0,            // SIPs completed
  window: 7,          // 7, or 8 after the first miss
  missed: 0,          // days skipped
  todayDone: false,
  hoursLeft: 14,      // hours left in today's SIP window
  broken: false,
  claim: 'none',      // none | claimable | claimed | credited
  hasCard: true,
  invested: 0,
  daysToJoin: 1,      // as of Aug 13, 2026 — joining closes Aug 14, 11:59 pm
  daysToEnd: 9,       // as of Aug 13, 2026 — campaign ends Aug 22
};

export const PRESETS = {
  teaser:    { phase: 'teaser', joined: false, done: 0, window: 7, missed: 0, todayDone: false, broken: false, claim: 'none', invested: 0, daysToJoin: 3, daysToEnd: 12 },
  join1:     { phase: 'join_open', joined: false, done: 0, window: 7, missed: 0, todayDone: false, broken: false, claim: 'none', invested: 0, daysToJoin: 3, daysToEnd: 12, hoursLeft: 14 },
  joinlast:  { phase: 'join_last', joined: false, done: 0, window: 7, missed: 0, todayDone: false, broken: false, claim: 'none', invested: 0, daysToJoin: 0, daysToEnd: 9, hoursLeft: 6 },
  closed:    { phase: 'join_closed', joined: false, done: 0, window: 7, missed: 0, todayDone: false, broken: false, claim: 'none', invested: 0, daysToJoin: -1, daysToEnd: 7 },
  final48:   { phase: 'final48', joined: false, done: 0, window: 7, missed: 0, todayDone: false, broken: false, claim: 'none', invested: 0, daysToJoin: -1, daysToEnd: 2 },
  ended:     { phase: 'ended', joined: false, done: 0, window: 7, missed: 0, todayDone: false, broken: false, claim: 'none', invested: 0, daysToJoin: -1, daysToEnd: 0 },
  d1done:    { phase: 'join_open', joined: true, done: 1, window: 7, missed: 0, todayDone: true, broken: false, claim: 'none', invested: 1000, daysToJoin: 3, daysToEnd: 12 },
  d3pending: { phase: 'join_open', joined: true, done: 2, window: 7, missed: 0, todayDone: false, hoursLeft: 9, broken: false, claim: 'none', invested: 2000, daysToJoin: 2, daysToEnd: 10 },
  atrisk:    { phase: 'join_closed', joined: true, done: 2, window: 7, missed: 0, todayDone: false, hoursLeft: 2, broken: false, claim: 'none', invested: 2000, daysToEnd: 8 },
  skip1:     { phase: 'join_closed', joined: true, done: 2, window: 8, missed: 1, todayDone: false, hoursLeft: 11, broken: false, claim: 'none', invested: 2000, daysToEnd: 7 },
  day8:      { phase: 'join_closed', joined: true, done: 6, window: 8, missed: 1, todayDone: false, hoursLeft: 5, broken: false, claim: 'none', invested: 6000, daysToEnd: 3 },
  broken:    { phase: 'join_closed', joined: true, done: 4, window: 8, missed: 2, todayDone: false, broken: true, claim: 'none', invested: 4000, daysToEnd: 5 },
  complete:  { phase: 'join_closed', joined: true, done: 7, window: 7, missed: 0, todayDone: true, broken: false, claim: 'claimable', invested: 7000, daysToEnd: 4 },
  claimed:   { phase: 'join_closed', joined: true, done: 7, window: 7, missed: 0, todayDone: true, broken: false, claim: 'claimed', invested: 7000, daysToEnd: 4 },
  credited:  { phase: 'ended', joined: true, done: 7, window: 7, missed: 0, todayDone: true, broken: false, claim: 'credited', invested: 7000, daysToEnd: 0 },
};

/* ---------------- derived ---------------- */
/* API-provided reward wins over the computed 2% fallback */
export const reward = (S) => (S.apiRewardInr != null ? S.apiRewardInr : Math.round(S.invested * 0.02));
export const dayNo = (S) => Math.min(S.window, S.done + S.missed + 1); // the day the user is on
export const canJoin = (S) => ['join_open', 'join_last'].includes(S.phase);
export const offersLive = (S) => S.phase !== 'ended';
export const streakLive = (S) => S.joined && !S.broken && S.claim === 'none' && S.done < TARGET;

/* ---------------- copy tables ---------------- */
export const DATE_STRIP = {
  teaser: 'Coming Aug 11th – Aug 22nd',
  join_open: 'From Aug 11th – Aug 22nd',
  join_last: 'From Aug 11th – Aug 22nd',
  join_closed: 'From Aug 11th – Aug 22nd',
  final48: 'Final 48 hours · ends Aug 22nd',
  ended: 'This campaign ended Aug 22nd',
};

/* returns {title, sub, badge:{cls,html}, alert:{cls,ic,html}|null, note, cta:{label,cls,act}}
   `act` is a string key the container maps to a handler. */
export function streakCopy(S) {
  const d = dayNo(S), r = reward(S);

  if (S.claim === 'credited') return {
    title: 'Streak complete', sub: "You did all 7 days. Here's what it paid.",
    badge: { cls: 'good', html: '<b>₹' + fmt(r) + ' credited</b> to SuperCoins' },
    alert: { cls: 'green', ic: '✓', html: '<b>₹' + fmt(r) + ' is in your wallet.</b> Thanks for going for gold.' },
    note: 'Your 2% reward has been credited.<br>Gold you bought stays in your vault.',
    cta: { label: 'View in wallet', cls: 'ghost', act: 'wallet' },
  };
  if (S.claim === 'claimed') return {
    title: 'Reward on the way', sub: 'Claim received. Credit lands within 24 hours.',
    badge: { cls: 'good', html: '<b>Claimed</b> · crediting within 24h' },
    alert: { cls: 'green', ic: '⏳', html: '<b>₹' + fmt(r) + " is being processed.</b> We'll push and email you the moment it lands." },
    note: 'Nothing more to do here.<br>You can keep buying gold at 5% back.',
    cta: { label: 'Track your reward', cls: 'ghost', act: 'earnings' },
  };
  if (S.claim === 'claimable') return {
    title: 'Streak complete', sub: 'All 7 daily SIPs done. Your 2% is ready.',
    badge: { cls: 'good', html: '🎁 <b>₹' + fmt(r) + '</b> ready to claim' },
    alert: { cls: 'green', ic: '🎁', html: '<b>Claim by ' + CLAIM_CLOSE + '.</b> Unclaimed rewards expire after that.' },
    note: 'You bought ₹' + fmt(S.invested) + ' of gold across 7 days.<br>That earns you 2% back in SuperCoins.',
    cta: { label: 'Claim ₹' + fmt(r), cls: '', act: 'claim' },
  };
  if (S.broken) return {
    title: 'Streak ended', sub: 'Two missed days ends a streak. The gold is still yours.',
    badge: { cls: 'bad', html: '✕ <b>Streak ended</b> on day ' + (S.done + S.missed) },
    alert: { cls: 'red', ic: '✕', html: '<b>The 2% streak reward is off the table.</b> Your 5% SuperCoins on each buy still applies.' },
    note: 'You bought ₹' + fmt(S.invested) + ' of gold before the streak broke.<br>Nothing you already earned is lost.',
    cta: { label: 'Buy gold anyway', cls: 'ghost', act: 'invest' },
  };
  if (S.joined && S.missed === 1 && !S.todayDone && S.done >= TARGET - 1) return {
    title: '7 days, 1 Goal', sub: 'One SIP left, and one day left to do it in.',
    badge: { cls: 'warn', html: '⚠️ <b>Last SIP, last day</b> · ' + S.hoursLeft + 'h left' },
    alert: { cls: 'red', ic: '⚠️', html: '<b>Miss today and the streak ends.</b> One SIP now locks in ₹' + fmt(Math.round((S.invested + DAILY_TARGET) * 0.02)) + '.' },
    note: 'Day ' + d + ' of 8 · ' + S.done + ' of ' + TARGET + ' SIPs done.<br>One more before 11:59 pm completes it.',
    cta: { label: 'Invest now · ' + S.hoursLeft + 'h left', cls: '', act: 'invest' },
  };
  if (S.joined && S.missed === 1) return {
    title: '7 days, 1 Goal', sub: 'You missed a day, so we added one. One chance left.',
    badge: { cls: 'warn', html: '＋1 <b>day added</b> · one chance left' },
    alert: { cls: 'amber', ic: '＋1', html: '<b>Day 8 unlocked.</b> Miss one more day and the streak ends for good.' },
    note: 'Day ' + d + ' of 8 · ' + S.done + ' of ' + TARGET + ' SIPs done.<br>Invest today to stay in.',
    cta: { label: 'Invest for day ' + d, cls: '', act: 'invest' },
  };
  if (S.joined && !S.todayDone && S.hoursLeft <= 3) return {
    title: '7 days, 1 Goal', sub: "Today's SIP is still pending.",
    badge: { cls: 'warn', html: '⏳ <b>' + S.hoursLeft + " hours left</b> for today's SIP" },
    alert: { cls: 'red', ic: '⏳', html: '<b>Miss today and you use up your one free skip.</b> A second miss ends the streak.' },
    note: 'Day ' + d + ' of 7 · closes at 11:59 pm.<br>₹200 is enough to keep it alive.',
    cta: { label: 'Invest now · ' + S.hoursLeft + 'h left', cls: '', act: 'invest' },
  };
  if (S.joined && !S.todayDone) return {
    title: '7 days, 1 Goal', sub: "Complete 7 daily Gold SIP's to<br><b>earn 2% in rewards.</b>",
    badge: { cls: '', html: '🔥 <b>' + S.done + ' day' + (S.done === 1 ? '' : 's') + '</b> streak going' },
    alert: { cls: 'amber', ic: '🔔', html: '<b>Day ' + d + ' is pending.</b>' },
    note: 'Day ' + d + ' of ' + S.window + ' · ' + S.done + ' of ' + TARGET + ' SIPs done.<br>Invest today to keep the streak.',
    cta: { label: 'Invest for day ' + d, cls: '', act: 'invest' },
  };
  if (S.joined && S.todayDone) return {
    title: '7 days, 1 Goal', sub: "Complete 7 daily Gold SIP's to<br><b>earn 2% in rewards.</b>",
    badge: { cls: 'good', html: '🔥 <b>' + S.done + ' day' + (S.done === 1 ? '' : 's') + '</b> streak going' },
    alert: { cls: 'green', ic: '✓', html: '<b>SIP ' + S.done + ' of ' + TARGET + ' done.</b> ' + (TARGET - S.done) + ' more to unlock 2% in rewards.' },
    note: 'Come back tomorrow before 11:59 pm.',
    cta: { label: 'Buy more gold', cls: 'ghost', act: 'invest' },
  };
  if (S.phase === 'teaser') return {
    title: '7 days, 1 Goal', sub: "Complete 7 daily Gold SIP's to<br><b>earn 2% in rewards.</b>",
    badge: { cls: '', html: '⌛ <b>Joining opens</b> Aug 11th' },
    alert: null,
    note: 'The streak starts Aug 11th.<br>Set a reminder and be day-one ready.',
    cta: { label: 'Remind me on Aug 11th', cls: 'ghost', act: 'remind' },
  };
  if (S.phase === 'join_last') return {
    title: '7 days, 1 Goal', sub: 'Last day to join. Starts the moment you invest.',
    badge: { cls: 'warn', html: '⌛ <b>Last day to join</b> · closes 11:59 pm' },
    alert: { cls: 'red', ic: '⌛', html: "<b>Joining closes tonight.</b> After this, the streak can't be started for this campaign." },
    note: 'Start today and your 7 days<br>still finish before Aug 22nd.',
    cta: { label: 'Join before 11:59 pm', cls: '', act: 'invest' },
  };
  if (S.phase === 'final48') return {
    title: 'Streak joining closed', sub: 'The streak is done, but the offers are not.',
    badge: { cls: 'warn', html: '⏳ <b>Final 48 hours</b> of the campaign' },
    alert: { cls: 'red', ic: '⏳', html: '<b>Everything closes Aug 22nd, 11:59 pm.</b> 5% back and ₹80-off vouchers end with it.' },
    note: 'Last chance to buy gold at 5% back<br>and use your ₹80-off vouchers.',
    cta: { label: 'Buy gold before it ends', cls: '', act: 'invest' },
  };
  if (S.phase === 'join_closed') return {
    title: 'Streak joining closed', sub: 'Joining ran Aug 11th – 14th. Gold offers are still on.',
    badge: { cls: 'bad', html: '<b>Joining closed</b> on Aug 14th' },
    alert: { cls: 'amber', ic: 'ℹ️', html: '<b>You can still buy gold at 5% back</b> and grab ₹80-off vouchers until Aug 22nd.' },
    note: 'The 7-day streak needed a start<br>by Aug 14th to finish in time.',
    cta: { label: 'Buy gold at 5% back', cls: 'ghost', act: 'invest' },
  };
  if (S.phase === 'ended') return {
    title: "That's a wrap", sub: 'Independence Week ended on Aug 22nd.',
    badge: { cls: 'bad', html: '<b>Campaign closed</b>' },
    alert: { cls: 'amber', ic: 'ℹ️', html: '<b>Rewards from completed streaks credit by Aug 25th.</b> Gold in your vault is unaffected.' },
    note: 'Thanks for going for gold.<br>Everyday 24K gold buying stays open.',
    cta: { label: 'Offer closed', cls: 'dead', act: 'none' },
  };
  /* default: join window open, not joined */
  return {
    title: '7 days, 1 Goal', sub: "Complete 7 daily Gold SIP's to<br><b>earn 2% in rewards.</b>",
    badge: { cls: '', html: '⌛ <b>' + S.daysToJoin + ' day' + (S.daysToJoin === 1 ? '' : 's') + '</b> left to join streak' },
    alert: null,
    note: 'Day 1 starts here.<br>Invest today to begin earning 2%.',
    cta: { label: "Let's Go", cls: '', act: 'sip' }, // native Gold SIP screen
  };
}

/* ---------------- ticker ---------------- */
export function pad(s) {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60,
    z = (n) => String(n).padStart(2, '0');
  return d > 0 ? d + 'd ' + z(h) + 'h ' + z(m) + 'm ' + z(sec) + 's' : z(h) + 'h ' + z(m) + 'm ' + z(sec) + 's';
}

export function countdownText(S) {
  const now = Date.now();
  /* API epoch timestamps override the static campaign dates */
  const start = S.startsOn ?? CAMPAIGN_START.getTime();
  const joinClose = S.joinEndsOn ?? JOIN_CLOSE.getTime();
  const end = S.endsOn ?? CAMPAIGN_END.getTime();
  if (S.phase === 'ended') return ['Campaign closed', 'rewards credit by Aug 25th', false];
  if (S.phase === 'teaser' || now < start) return ['Go For Gold starts in', pad(Math.max(0, Math.floor((start - now) / 1000))), false];
  if (canJoin(S) && !S.joined) return ['Joining closes in', pad(Math.max(0, Math.floor((joinClose - now) / 1000))), S.phase === 'join_last'];
  const s = Math.max(0, Math.floor((end - now) / 1000));
  return ['Offer ends in', pad(s), s < 172800];
}

export function tickerMessages(S) {
  if (S.phase === 'ended') return ['Independence Week has ended · thanks for going for gold',
    'Completed streaks: 2% credits by Aug 26th', '24K gold buying stays open, year-round'];
  if (S.broken) return ['Streak ended · 5% back in SuperCoins still applies on every buy',
    '₹80 off vouchers over ₹800 · until Aug 26th', 'gold delivery at ₹1/-, all campaign long'];
  if (S.claim === 'claimable') return ['Your 2% reward is ready to claim', 'Claim before Aug 29th or it expires',
    '₹80 off vouchers over ₹800 · until Aug 26th'];
  if (S.claim === 'claimed') return ['Reward claimed · crediting within 24 hours', "We'll push and email you when it lands"];
  if (S.claim === 'credited') return ['₹' + fmt(reward(S)) + ' credited to your SuperCoins', '24K gold buying stays open, year-round'];
  if (S.joined && S.missed === 1) return ['One day missed · day 8 added, one chance left',
    'Miss another and the 2% reward is gone', '₹200 is enough to keep the streak alive'];
  if (S.joined && !S.todayDone) return ["Today's SIP is still pending · closes 11:59 pm",
    '₹200 is enough to keep the streak alive', '5% back in SuperCoins on every gold buy'];
  if (S.phase === 'join_last') return ['Last day to join the streak · closes 11:59 pm',
    '80 years of freedom · ₹80 off vouchers over ₹800', '5% back in SuperCoins on every gold buy'];
  if (S.phase === 'join_closed') return ['Streak joining closed on Aug 14th · offers run to Aug 22nd',
    '80 years of freedom · ₹80 off vouchers over ₹800', '5% back in SuperCoins on every gold buy'];
  if (S.phase === 'final48') return ['Final 48 hours of Independence Week',
    '₹80 off vouchers over ₹800 · ends Aug 22nd', '5% back in SuperCoins · ends Aug 22nd'];
  if (S.phase === 'teaser') return ['Go For Gold opens Aug 14th', '7 days, 1 goal · 2% rewards on a SIP streak',
    '80 years of freedom · ₹80 off vouchers over ₹800'];
  return ['7 days, 1 goal · 2% rewards on your SIP streak', '80 years of freedom · ₹80 off vouchers over ₹800',
    'Earn 5% back in SuperCoins on every gold buy', 'Gold delivery at ₹1/-, all campaign long'];
}

/* gold amount → display string, e.g. 100mg / 1.00g
   Uses the live API rate (mg per rupee) when available. */
export const goldQty = (amount, mgPerRupee) => {
  const mg = mgPerRupee ? amount * mgPerRupee : amount / RATE;
  return mg >= 1000 ? (mg / 1000).toFixed(2) + 'g' : mg.toFixed(2) + 'mg';
};
